import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { FounderGuestRoute } from "./components/FounderGuestRoute";
import { FounderProtectedRoute } from "./components/FounderProtectedRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PublisherGuestRoute } from "./components/PublisherGuestRoute";
import { FounderLayout } from "./layouts/FounderLayout";
import { PublicLayout } from "./layouts/PublicLayout";
import { AppDetailPage } from "./pages/AppDetailPage";
import { AppInstallationGuidePage } from "./pages/AppInstallationGuidePage";
import { AppInstallationGuideStepsPage } from "./pages/AppInstallationGuideStepsPage";
import { CategoriesPage } from "./pages/CategoriesPage";
import { ContactPage } from "./pages/ContactPage";
import { DiscoverAppsPage } from "./pages/DiscoverAppsPage";
import { FounderSectionWorkspace } from "./pages/founder/FounderSectionWorkspace";
import { FounderLogInPage } from "./pages/founder/FounderLogInPage";
import { FounderSetPasswordPage } from "./pages/founder/FounderSetPasswordPage";
import { HomePage } from "./pages/HomePage";
import { LogInPage } from "./pages/LogInPage";
import { MembershipPage } from "./pages/MembershipPage";
import { PrivacyPoliciesPage } from "./pages/PrivacyPoliciesPage";
import { PublishYourAppPage } from "./pages/PublishYourAppPage";
import { PublisherDashboardPage } from "./pages/PublisherDashboardPage";
import { SignInPage } from "./pages/SignInPage";
import { UpdateAppPage } from "./pages/UpdateAppPage";

function LegacyAppListRedirect() {
  const { segment } = useParams<{ segment: string }>();
  const tab = segment ?? "all";
  return <Navigate replace to={`/founder/hub/apps?tab=${encodeURIComponent(tab)}`} />;
}

function LegacyAppDetailRedirect() {
  const { segment, applicationId } = useParams<{ segment: string; applicationId: string }>();
  const tab = segment ?? "all";
  const app = applicationId ? `&app=${encodeURIComponent(applicationId)}` : "";
  return <Navigate replace to={`/founder/hub/apps?tab=${encodeURIComponent(tab)}${app}`} />;
}

function LegacyPublisherDetailRedirect() {
  const { publisherId } = useParams<{ publisherId: string }>();
  const publisher = publisherId ? `?publisher=${encodeURIComponent(publisherId)}` : "";
  return <Navigate replace to={`/founder/hub/publishers${publisher}`} />;
}

export default function App() {
  return (
    <Routes>
      <Route
        path="founder/log-in"
        element={
          <FounderGuestRoute>
            <FounderLogInPage />
          </FounderGuestRoute>
        }
      />
      <Route
        path="founder/set-password"
        element={
          <FounderGuestRoute>
            <FounderSetPasswordPage />
          </FounderGuestRoute>
        }
      />

      <Route
        path="founder"
        element={
          <FounderProtectedRoute>
            <FounderLayout />
          </FounderProtectedRoute>
        }
      >
        <Route index element={<Navigate replace to="hub/overview" />} />
        <Route path="hub" element={<Navigate replace to="hub/overview" />} />
        <Route path="hub/:sectionId" element={<FounderSectionWorkspace />} />

        <Route path="overview/dashboard" element={<Navigate replace to="/founder/hub/overview" />} />
        <Route path="overview/activity" element={<Navigate replace to="/founder/hub/overview" />} />
        <Route path="traffic/analytics" element={<Navigate replace to="/founder/hub/traffic" />} />

        <Route path="apps/pending" element={<Navigate replace to="/founder/hub/apps?tab=pending" />} />
        <Route path="apps/published" element={<Navigate replace to="/founder/hub/apps?tab=published" />} />
        <Route path="apps/rejected" element={<Navigate replace to="/founder/hub/apps?tab=rejected" />} />
        <Route path="apps/all" element={<Navigate replace to="/founder/hub/apps?tab=all" />} />
        <Route path="apps/:segment/:applicationId" element={<LegacyAppDetailRedirect />} />
        <Route path="apps/:segment" element={<LegacyAppListRedirect />} />

        <Route path="publishers/list" element={<Navigate replace to="/founder/hub/publishers" />} />
        <Route path="publishers/list/:publisherId" element={<LegacyPublisherDetailRedirect />} />

        <Route path="memberships/active" element={<Navigate replace to="/founder/hub/memberships" />} />
        <Route path="memberships/plans" element={<Navigate replace to="/founder/hub/memberships" />} />
        <Route path="memberships/payments" element={<Navigate replace to="/founder/hub/memberships" />} />

        <Route path="marketplace/categories" element={<Navigate replace to="/founder/hub/marketplace" />} />

        <Route path="platform/health" element={<Navigate replace to="/founder/hub/platform?tab=health" />} />
        <Route path="platform/security" element={<Navigate replace to="/founder/hub/platform?tab=security" />} />
        <Route path="platform/publishing" element={<Navigate replace to="/founder/hub/platform?tab=publishing" />} />
        <Route path="platform/legal" element={<Navigate replace to="/founder/hub/platform?tab=legal" />} />
        <Route path="platform/audit" element={<Navigate replace to="/founder/hub/platform?tab=audit" />} />

        <Route path="account/profile" element={<Navigate replace to="/founder/hub/account" />} />
        <Route path="account/security" element={<Navigate replace to="/founder/hub/account" />} />
      </Route>

      <Route element={<PublicLayout />}>
        <Route index element={<HomePage />} />

        <Route path="app-installation-guide">
          <Route index element={<AppInstallationGuidePage />} />
          <Route path="steps" element={<AppInstallationGuideStepsPage />} />
        </Route>

        <Route path="membership" element={<MembershipPage />} />

        <Route
          path="sign-in"
          element={
            <PublisherGuestRoute>
              <SignInPage />
            </PublisherGuestRoute>
          }
        />

        <Route
          path="log-in"
          element={
            <PublisherGuestRoute>
              <LogInPage />
            </PublisherGuestRoute>
          }
        />

        <Route
          path="publish-your-app/:applicationId/edit"
          element={
            <ProtectedRoute>
              <PublishYourAppPage />
            </ProtectedRoute>
          }
        />

        <Route path="publish-your-app" element={<PublishYourAppPage />} />

        <Route path="apps/:slug" element={<AppDetailPage />} />

        <Route path="discover-apps" element={<DiscoverAppsPage />} />

        <Route path="categories" element={<CategoriesPage />} />

        <Route
          path="publisher/apps/:applicationId/update"
          element={
            <ProtectedRoute>
              <UpdateAppPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="publisher-dashboard"
          element={
            <ProtectedRoute>
              <PublisherDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route path="privacy-policies" element={<PrivacyPoliciesPage />} />

        <Route path="contact" element={<ContactPage />} />

        <Route path="*" element={<Navigate replace to="/" />} />
      </Route>
    </Routes>
  );
}
